import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridColDef, GridToolbarQuickFilter } from "@mui/x-data-grid";
import "./BniOverview.css";
import { useTranslation } from "react-i18next";
import { FetchBinReport } from "../../ApiServices/ApiRequests";
import {
  BinOverviewReportInfoResponse,
  BinReportInfo,
  SitesInfo,
} from "../../Models/Models";
import { columns, getRowClassName } from "./BinOverviewUtiles";
import BinReportCard from "./ReportCards/BinReportCards";
import {
  useWasteManagement,
  WasteManagementType,
} from "../../ContextAPI/WasteManagementContext";
import dayjs, { Dayjs } from "dayjs";
import ExportBinPage from "./ReportExport/BinExport";
import HeaderComponent from "../HeaderComponent/HeaderComponent";
import { useLocation } from "react-router-dom";
import Loading from "../../Controller/LoadingController/Loading";
import { useMasterData } from "../../ContextAPI/MasterContext";

export default function BinOverviewReport() {
  const { masterState } = useMasterData();
  const { wasteManagementState, dispatchWasteManagement } =
    useWasteManagement();
  const { siteData, pageSize, pageNo } = wasteManagementState;
  const [loading, setloading] = useState<boolean>(false);

  const handleInputChange = useCallback(
    (
      field: keyof WasteManagementType,
      value: BinReportInfo[] | number | boolean
    ) => {
      dispatchWasteManagement({ type: "SET_FIELD", field, value });
    },
    [dispatchWasteManagement]
  );

  const handleBulkUpdate = useCallback(
    (value: Partial<WasteManagementType>) => {
      dispatchWasteManagement({ type: "BULK_UPDATE", value });
    },
    [dispatchWasteManagement]
  );

  const { t } = useTranslation();

  const fetchData = async (
    siteCode: string,
    pageNo: number,
    pageSize: number,
    fromDate: string | null,
    groupId: string
  ): Promise<void> => {
    try {
      console.log("entering");
      setloading(true)
      const data: BinOverviewReportInfoResponse = await FetchBinReport(
        siteCode,
        pageNo,
        pageSize,
        fromDate,
        groupId
      );
      const reportData =
        data?.content && data?.content?.length > 0
          ? {
              siteData:
                data.content.map((item, index) => ({
                  ...item,
                  id: index,
                })) || [], // Ensure siteData is an empty array if report is undefined
            }
          : {
              siteData: [], // Set siteData as an empty array if content is empty
            };

      var wasteDataSet: Partial<WasteManagementType> = {};
      wasteDataSet.siteData = reportData.siteData;
      wasteDataSet.totalElements = data.totalElements;

      handleBulkUpdate(wasteDataSet);
    } catch (error) {
      console.error("Error loading site data", error);
    } finally {
      setloading(false)
    }
  };

  useEffect(() => {
    const formattedDate: string | null = masterState.reportSelectedDate
      ? masterState.reportSelectedDate.format("YYYY-MM-DD")
      : "";

    if (masterState.selectedSiteCode.length > 0) {
      fetchData(
        masterState.selectedSiteCode,
        wasteManagementState.pageNo,
        wasteManagementState.pageSize,
        formattedDate,
        masterState.selectedGroupCode
      );
    }
  }, [
    masterState.selectedSiteCode,
    masterState.selectedGroupCode,
    masterState.reportSelectedDate,
    wasteManagementState.pageNo,
    wasteManagementState.pageSize,
  ]);

  const columData: GridColDef[] = useMemo(
    () => [
      {
        field: "deviceName",
        headerName: t("global.deviceName"),

        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },
      {
        field: "location",
        headerName: t("global.Location"),

        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },
      {
        field: "groupName",
        headerName: t("global.GroupName"),

        headerAlign: "center",
        align: "center",
        flex: 0.5,
        // renderCell: (params) => (params.value === true ? "Yes" : "No"),
      },
      {
        field: "status",
        headerName: t("global.status"),

        headerAlign: "center",
        align: "center",
        flex: 0.5,
        renderCell: (params) => {
          let color;
          switch (params.value) {
            case "Clean":
              color = "green";
              break;
            case "Critical":
              color = "red";
              break;
            case "Moderate":
              color = "orange";
              break;
            default:
              color = "inherit";
          }

          return <span style={{ color }}>{params.value}</span>;
        },
      },
      {
        field: "level",
        headerName: t("global.FillLevel(%)"),

        headerAlign: "center",
        align: "center",
        flex: 0.5,
        renderCell: (params) =>
          params.value || params.value === 0 ? `${params.value}%` : "-",
      },
      {
        field: "lastCollectedDate",
        headerName: t("global.LastCollected"),

        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },

      {
        field: "nextCollectionDate",
        headerName: t("global.NextCollected"),

        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },

      {
        field: "actionTaken",
        headerName: t("global.ActionTaken"),

        headerAlign: "center",
        align: "center",
        flex: 0.5,
      },
    ],
    []
  );
  return (
    <Box sx={{ width: "100%", mt: 10.5}}>
      <HeaderComponent />
      <BinReportCard />
      <ExportBinPage />
      {loading ? (
        <Loading loading={loading} type={`dots`} backDrop={false} />
      ) : (
        <Box className="data-grid-container">
          <DataGrid
            rows={siteData}
            disableAutosize
            disableRowSelectionOnClick
            disableColumnResize
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableColumnSorting
            disableColumnMenu
            localeText={{
              noRowsLabel: "No Data Found",
            }}
            columns={columData}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
            getRowClassName={getRowClassName}
            paginationMode="server" // Enable server-side pagination
            paginationModel={{ pageSize, page: pageNo }} // Use paginationModel
            onPaginationModelChange={(model) => {
              handleInputChange("pageSize", model.pageSize);
              handleInputChange("pageNo", model.page);
            }}
            rowCount={wasteManagementState.totalElements}
            pageSizeOptions={[5, 10, 25, 50, 75, 100]}
          />
        </Box>
      )}
    </Box>
  );
}
